import { useEffect, useState } from "react";
import * as Yup from "yup";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { Stack, IconButton, InputAdornment, Alert } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// auth
import { useAuthContext } from "../../auth/useAuthContext";
// components
import Iconify from "../../components/iconify";
import FormProvider, { RHFTextField } from "../../components/hook-form";
import ReCAPTCHA from "react-google-recaptcha";

// ----------------------------------------------------------------------

type FormValuesProps = {
  accountName: string;
  email: string;
  phoneNumber: string;
  password: string;
  firstName: string;
  lastName: string;
  afterSubmit?: string;
};

const phoneRegExp =
  /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;

export default function AuthRegisterForm() {
  const { register } = useAuthContext();
  const [pushToken, setPushToken] = useState<string>();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);

  const RegisterSchema = Yup.object().shape({
    accountName: Yup.string().required("El nombre de la cuenta es requerido"),
    firstName: Yup.string().required("El nombre es requerido"),
    lastName: Yup.string().required("El apellido es requerido"),
    email: Yup.string()
      .required("El correo es requerido")
      .email("El correo debe ser válido"),
    phoneNumber: Yup.string()
      .required("El teléfono es requerido")
      .matches(phoneRegExp, "El teléfono no es válido"),
    password: Yup.string().required("La contraseña es requerida"),
  });

  const defaultValues = {
    accountName: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      if (!captchaToken) {
        setError("afterSubmit", {
          message:
            "Se debe validar el captcha para registrarse. Por favor, vuelva a intentarlo.",
        });
        return;
      }

      if (register) {
        await register(
          data.accountName,
          data.email,
          data.password,
          data.firstName,
          data.lastName,
          data.phoneNumber,
          pushToken as string,
          captchaToken
        );
      }
    } catch (error) {
      console.error(error);
      reset();
      setError("afterSubmit", {
        ...error,
        message: error.message || error,
      });
    }
  };

  function onChangeCaptcha(value: string | null) {
    setCaptchaToken(value);
  }

  useEffect(() => {
    setPushToken((window as any).pushToken);
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2.5}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        <RHFTextField
          name="accountName"
          label="Nombre de la cuenta"
          size="small"
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <RHFTextField name="firstName" label="Nombre" size="small" />
          <RHFTextField name="lastName" label="Apellido" size="small" />
        </Stack>

        <RHFTextField name="email" label="Email" size="small" />

        <RHFTextField name="phoneNumber" label="Teléfono" size="small" />

        <RHFTextField
          name="password"
          label="Contraseña"
          size="small"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <ReCAPTCHA
          sitekey={process.env.REACT_APP_CAPTCHA_KEY || ""}
          onChange={onChangeCaptcha}
        />

        <LoadingButton
          fullWidth
          type="submit"
          variant="contained"
          loading={isSubmitSuccessful || isSubmitting}
          sx={{
            color: (theme) =>
              theme.palette.mode === "light" ? "common.white" : "grey.800",
            "&:hover": {
              color: (theme) =>
                theme.palette.mode === "light" ? "common.white" : "grey.800",
            },
          }}
        >
          Crear cuenta
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
